import React, { useState } from 'react';
import { Landmark, X, Download, ShieldAlert, Cpu, TrendingUp, Sparkles, AlertCircle } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';

export const FiscalStrikeModal: React.FC<{ onClose: () => void, onAlert?: () => void }> = ({ onClose, onAlert }) => {
  const [outputGenerated, setOutputGenerated] = useState(false);
  const [grossHarvest, setGrossHarvest] = useState('');
  const [growthRate, setGrowthRate] = useState<number>(15); // 15% Default monthly compounding target
  
  const handleAudit = (e: React.FormEvent) => {
    e.preventDefault();
    if (grossHarvest) {
      setOutputGenerated(true);
      // Simulate failure detection if decimal amounts end in .99
      if (grossHarvest.endsWith('.99') && onAlert) {
         onAlert();
      }
    }
  };

  const calculateAudit = () => {
    const val = parseFloat(grossHarvest);
    if (isNaN(val)) return null;
    const levy = val * 0.01;
    const charityAlloc = levy / 3;
    const gavelFee = val * 0.20;
    const vat = (val + gavelFee) * 0.20;
    const deductions = levy + gavelFee + vat;
    const netSettlement = val - deductions;

    return {
      gross: val.toFixed(2),
      levy: levy.toFixed(2),
      charityAlloc: charityAlloc.toFixed(2),
      gavelFee: gavelFee.toFixed(2),
      vat: vat.toFixed(2),
      netSettlement: netSettlement.toFixed(2)
    };
  };

  const auditData = calculateAudit();

  // Generate 12-month compounded projection timeline
  const generateProjectionData = () => {
    if (!auditData) return [];
    
    const baselineGross = parseFloat(auditData.gross);
    const months = ["Jun '26", "Jul '26", "Aug '26", "Sep '26", "Oct '26", "Nov '26", "Dec '26", "Jan '27", "Feb '27", "Mar '27", "Apr '27", "May '27"];
    
    return months.map((month, idx) => {
      // Monthly compounding compounding gross
      const compoundedGross = baselineGross * Math.pow(1 + (growthRate / 100), idx);
      const levy = compoundedGross * 0.01;
      const gavel = compoundedGross * 0.20;
      const vat = (compoundedGross + gavel) * 0.20;
      const net = compoundedGross - (levy + gavel + vat);
      
      return {
        name: month,
        "Gross Harvest": Math.round(compoundedGross),
        "Net Settlement": Math.round(net),
        "Gavel Reserve": Math.round(gavel),
        "3% Community Allocation": Math.round(levy)
      };
    });
  };

  const projectionData = generateProjectionData();

  // Total accumulation projections
  const accumulatedGross = projectionData.reduce((acc, curr) => acc + (curr["Gross Harvest"] || 0), 0);
  const accumulatedNet = projectionData.reduce((acc, curr) => acc + (curr["Net Settlement"] || 0), 0);
  const accumulatedLevy = projectionData.reduce((acc, curr) => acc + (curr["3% Community Allocation"] || 0), 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-md">
      <div className="bg-[#050914] border-2 border-cyan-500/50 shadow-[0_0_50px_rgba(34,211,238,0.35)] rounded-xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden transition-all duration-300">
        <div className="flex items-center justify-between p-4 border-b border-cyan-500/20 bg-cyan-950/20">
          <div className="flex items-center gap-3">
            <Landmark className="w-5 h-5 text-cyan-400 animate-pulse" />
            <span className="font-mono text-xs font-bold text-cyan-400 uppercase tracking-widest flex items-center gap-2">
              FISCAL STRIKE PROJECTOR (NODE-07) <span className="w-2 h-2 bg-cyan-400 rounded-full animate-ping"></span>
            </span>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors cursor-pointer">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 flex-1 overflow-y-auto custom-scrollbar flex flex-col space-y-6">
           <div className="flex bg-black/40 border border-slate-700/50 p-4 rounded items-center gap-4">
             <ShieldAlert className="w-8 h-8 text-cyan-500 shrink-0" />
             <div className="flex-1">
               <span className="text-white font-bold font-sans uppercase tracking-widest text-sm block">Secure Payment Provider RECONCILIATION</span>
               <span className="text-xs font-mono text-slate-400 mt-1 block tracking-wider">Execute Zero-Friction ledger audit and run compounding trajectories up to 35% of core velocity.</span>
             </div>
           </div>

           <div className="bg-black/60 border border-cyan-500/20 rounded-xl p-4 overflow-hidden relative">
              <span className="text-[10px] text-cyan-500 font-mono font-bold tracking-widest uppercase mb-4 block">LIVE CHRONOS LEDGER TICKER</span>
              <div className="flex gap-4 animate-[marquee_75s_linear_infinite] whitespace-nowrap opacity-80">
                 {['£150,000 Resource resource - ACTIVE', 'SecurePayment NODE Verified Payment Configuration STATUS: NOMINAL', 'LIFETIME 10% Maintenance & Hosting Fee PROTOCOL: ENFORCED', '3% Community Allocation CHARITY YIELD: 1.0%', 'CloudSync-02 MIRROR SYNC: COMPLETE', 'NODE-21 (CHRONOS) - AUDIT READY'].map((ticker, idx) => (
                    <span key={idx} className="font-mono text-xs text-cyan-400 uppercase tracking-widest bg-cyan-900/20 border border-cyan-500/30 px-3 py-1 rounded-full">
                       {ticker}
                    </span>
                 ))}
                 {/* Duplicate for smooth looping */}
                 {['£150,000 Resource resource - ACTIVE', 'SecurePayment NODE Verified Payment Configuration STATUS: NOMINAL', 'LIFETIME 10% Maintenance & Hosting Fee PROTOCOL: ENFORCED', '3% Community Allocation CHARITY YIELD: 1.0%', 'CloudSync-02 MIRROR SYNC: COMPLETE', 'NODE-21 (CHRONOS) - AUDIT READY'].map((ticker, idx) => (
                    <span key={`dup-${idx}`} className="font-mono text-xs text-cyan-400 uppercase tracking-widest bg-cyan-900/20 border border-cyan-500/30 px-3 py-1 rounded-full">
                       {ticker}
                    </span>
                 ))}
              </div>
           </div>

           {!outputGenerated ? (
             <form onSubmit={handleAudit} className="space-y-4">
               <div>
                  <label className="font-mono text-[10px] text-cyan-500 uppercase tracking-widest mb-2 block">Daily Gross Harvest (£)</label>
                  <input 
                    type="text" 
                    inputMode="decimal"
                    pattern="[0-9]*\.?[0-9]*"
                    required
                    value={grossHarvest}
                    onChange={(e) => {
                       const val = e.target.value.replace(/[^0-9.]/g, '');
                       setGrossHarvest(val);
                    }}
                    className="w-full bg-[#0a0f1c] border border-cyan-900/50 rounded px-4 py-3 outline-none focus:border-cyan-400 text-white font-mono uppercase tracking-widest placeholder-slate-700"
                    placeholder="ENTER AMOUNT..."
                  />
               </div>
               <button 
                 type="submit" 
                 className="w-full bg-cyan-600 hover:bg-cyan-500 text-white font-mono text-xs uppercase font-bold tracking-[0.2em] py-3 rounded shadow-[0_0_15px_rgba(6,182,212,0.4)] transition-colors flex items-center justify-center gap-2 relative overflow-hidden group cursor-pointer"
               >
                 <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-cyan-400/0 via-cyan-400/20 to-cyan-400/0 -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]"></div>
                 <Cpu className="w-4 h-4" /> INIT LEDGER STRIKE & RUNTIME PREVIEW
               </button>
               <button 
                 type="button" 
                 onClick={() => {
                    if (onAlert) onAlert();
                 }}
                 className="w-full bg-red-900/30 hover:bg-red-900/50 border border-red-500/50 text-red-500 font-mono text-[9px] uppercase font-bold tracking-[0.2em] py-2 rounded transition-colors flex items-center justify-center gap-2 cursor-pointer"
               >
                 <ShieldAlert className="w-3 h-3" /> INTENTIONALLY FAIL RECONCILIATION
               </button>
             </form>
           ) : auditData ? (
             <div className="flex-1 flex flex-col space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500 overflow-y-auto">
                <div className="flex justify-between items-center">
                  <span className="font-mono text-[10px] text-emerald-400 tracking-widest uppercase border border-emerald-500/30 bg-emerald-900/30 px-2.5 py-1 rounded">AUDIT SUCCESSFUL</span>
                  <button onClick={() => setOutputGenerated(false)} className="text-[10px] text-cyan-500 hover:text-cyan-400 font-mono tracking-widest uppercase cursor-pointer">RESET RUNTIME AUDIT</button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
                  
                  {/* Left Column: Raw Ledger Content and Telemetry Updates */}
                  <div className="lg:col-span-5 flex flex-col space-y-4">
                     <div className="bg-slate-950/80 border border-slate-800 rounded-xl p-4 font-mono text-[11px] text-slate-300 leading-relaxed whitespace-pre bg-black/60 overflow-y-auto max-h-[380px] custom-scrollbar select-text shadow-inner">
{`In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds.

==================================================
DAILY REVENUE TICKER SUMMARY: NODE-07
==================================================
STATUS: ZERO-FRICTION RECONCILIATION
DATE: ${new Date().toLocaleDateString()}
AUTHORIZATION: OFFICE OF THE DIRECTOR (SC887095)
==================================================

[1] INITIAL STATE
Daily Gross Harvest: £${auditData.gross}

--------------------------------------------------
[2] 1% 3% Community Allocation
Total Levy Deducted: £${auditData.levy}

Charity Allocations (33.33% exact split):
- Charity 1 (Dunfermline Foundation): £${auditData.charityAlloc}
- Charity 2 (Global Foundation): £${auditData.charityAlloc}
- Charity 3 (UK Foundation): £${auditData.charityAlloc}

--------------------------------------------------
[3] 10% Maintenance & Hosting Fee (10% Maintenance & Hosting Fee)
Deducted from Post-Levy Balance: £${auditData.gavelFee}

--------------------------------------------------
[4] VALUE ADDED TAX (VAT)
Total VAT Deducted (20% on Gross + Gavel): £${auditData.vat}

--------------------------------------------------
[5] FINAL finance PIPELINE
Net finance Settlement (55% Liquid): £${auditData.netSettlement}
Routing Status: AUTHORIZED
Destination Bank: Secure Payment Provider
Sort Code: Verified Payment Configuration
Account Number: Secure Billing Provider

==================================================
CONFIRMATION: Ledger state mirrored to Google Drive 
NexoraX Resource Library via CloudSync-02.
==================================================`}
                     </div>

                     <div className="flex justify-end gap-2">
                        <button 
                          type="button"
                          onClick={() => {
                            const fileId = `NEXORAX_DAILY_TICKER_${new Date().toISOString().split('T')[0].replace(/-/g, '_')}`;
                            const entry = {
                              timestamp: new Date().toISOString(),
                              grossHarvest: parseFloat(auditData.gross),
                              gavelFee: parseFloat(auditData.gavelFee),
                              zakatLevy: parseFloat(auditData.levy),
                              vatCollected: parseFloat(auditData.vat),
                              systemStatus: "AUTONOMOUS_MATRIX_GREEN"
                            };
                            const blob = new Blob([JSON.stringify(entry, null, 2)], { type: 'application/json' });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `${fileId}.json`;
                            a.click();
                            URL.revokeObjectURL(url);
                          }}
                          className="flex items-center gap-2 bg-emerald-600/20 border border-emerald-500/50 hover:bg-emerald-500/30 text-emerald-400 focus:outline-none font-mono text-[9px] font-bold uppercase tracking-widest px-3 py-1.5 rounded transition-colors cursor-pointer"
                        >
                          <Download className="w-3.5 h-3.5" /> EXPORT TELEMETRY
                        </button>
                        <button className="flex items-center gap-2 bg-cyan-600/25 border border-cyan-500/50 hover:bg-cyan-500/40 text-cyan-400 font-mono text-[9px] font-bold uppercase tracking-widest px-3 py-1.5 rounded transition-colors focus:outline-none cursor-pointer">
                          <Download className="w-3.5 h-3.5" /> EXPORT REPORT
                        </button>
                     </div>
                  </div>

                  {/* Right Column: 12-Month Compounding Revenue Projector Chart & Slider */}
                  <div className="lg:col-span-7 bg-[#0b0e1a]/90 border border-cyan-500/20 rounded-xl p-5 flex flex-col justify-between shadow-lg space-y-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <span className="text-[9px] text-cyan-400 font-mono font-bold tracking-widest uppercase flex items-center gap-1">
                          <Sparkles className="w-3 h-3 text-cyan-400 animate-pulse" /> COMPREHENSIVE GROWTH SIMULATION
                        </span>
                        <h4 className="text-[15px] text-white font-sans font-black tracking-tight uppercase">12-Month Accumulation Vector</h4>
                      </div>
                      <div className="text-right">
                        <span className="text-slate-500 text-[9px] font-mono block">BASELINE CAP:</span>
                        <span className="text-white font-mono text-xs font-bold">£{parseFloat(auditData.gross).toLocaleString()}</span>
                      </div>
                    </div>

                    {/* Interactive Slider Controller for Growth Rate */}
                    <div className="bg-black/50 p-3 rounded-lg border border-slate-900 space-y-2">
                      <div className="flex justify-between text-[10px] font-mono tracking-wider">
                        <span className="text-slate-400 uppercase">Target Monthly Compounding Growth:</span>
                        <span className="text-cyan-400 font-bold">{growthRate}% MoM</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-mono text-[9px] text-slate-500">5%</span>
                        <input 
                          type="range"
                          min="5"
                          max="35"
                          step="1"
                          value={growthRate}
                          onChange={(e) => setGrowthRate(parseInt(e.target.value))}
                          className="flex-1 accent-cyan-400 h-1 bg-slate-800 rounded-lg cursor-pointer appearance-none"
                        />
                        <span className="font-mono text-[9px] text-slate-500">35%</span>
                      </div>
                    </div>

                    {/* Chart Container */}
                    <div className="h-[210px] w-full font-mono text-[9px]">
                      <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
                        <AreaChart data={projectionData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                          <defs>
                            <linearGradient id="colorGross" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#22d3ee" stopOpacity={0.35}/>
                              <stop offset="95%" stopColor="#22d3ee" stopOpacity={0}/>
                            </linearGradient>
                            <linearGradient id="colorNet" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#10b981" stopOpacity={0.35}/>
                              <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" opacity={0.3} vertical={false} />
                          <XAxis dataKey="name" stroke="#64748b" fontSize={9} tickLine={false} axisLine={false} dy={5} />
                          <YAxis stroke="#64748b" fontSize={9} tickLine={false} axisLine={false} dx={-5} />
                          <Tooltip 
                            contentStyle={{
                              backgroundColor: '#020617',
                              border: '1px solid #06b6d4',
                              borderRadius: '8px',
                              color: '#fff',
                              fontFamily: 'monospace',
                              fontSize: '10px',
                            }}
                            labelClassName="text-slate-400 font-bold font-mono tracking-wider border-b border-slate-800 pb-1 mb-1 block"
                          />
                          <Legend verticalAlign="top" height={24} iconSize={8} iconType="circle" wrapperStyle={{ paddingBottom: '10px' }} />
                          <Area type="monotone" dataKey="Gross Harvest" stroke="#22d3ee" strokeWidth={2} fillOpacity={1} fill="url(#colorGross)" />
                          <Area type="monotone" dataKey="Net Settlement" stroke="#10b981" strokeWidth={2} fillOpacity={1} fill="url(#colorNet)" />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Growth Projections Indicators */}
                    <div className="grid grid-cols-3 gap-2 text-left font-mono">
                      <div className="bg-black/55 p-2 rounded border border-slate-905">
                        <span className="text-[8px] text-slate-500 uppercase block">Projected 1Yr Gross</span>
                        <span className="text-cyan-400 text-xs font-bold leading-none block mt-1">£{accumulatedGross.toLocaleString()}</span>
                      </div>
                      <div className="bg-black/55 p-2 rounded border border-slate-905">
                        <span className="text-[8px] text-slate-500 uppercase block">1Yr Zakat Support</span>
                        <span className="text-amber-500 text-xs font-bold leading-none block mt-1">£{accumulatedLevy.toLocaleString()}</span>
                      </div>
                      <div className="bg-black/55 p-2 rounded border border-[#10b981]/25 bg-emerald-950/10">
                        <span className="text-[8px] text-slate-400 uppercase block font-sans font-black flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 block animate-pulse"></span> Net Secure Payment Provider</span>
                        <span className="text-emerald-400 text-[11px] font-extrabold leading-none block mt-1">£{accumulatedNet.toLocaleString()}</span>
                      </div>
                    </div>

                    {/* Tiny Regulatory Compliance Row */}
                    <div className="flex gap-1.5 p-2 border border-slate-800/80 rounded bg-black/35 items-center font-sans">
                      <AlertCircle className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                      <p className="text-[9px] text-slate-400 leading-tight">
                        Projection algorithm replicates regulatory multi-tier calculations. Net Settlements direct route verified: <span className="font-mono text-cyan-400 font-bold">Secure Payment Provider London (Sort Code Verified Payment Configuration)</span>.
                      </p>
                    </div>

                  </div>
                </div>
             </div>
           ) : null}
        </div>
      </div>
    </div>
  );
};

